import { useAppDispatch } from '@/hooks';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Key, ReactNode, useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { ProjectStatusTag } from '@pages/project-management/components/ProjectStatusTag';

import AppTooltip from '@/components/app-tooltip/AppTooltip';
import { BaseButton } from '@/components/base-button/BaseButton';
import { showBasePopup } from '@/components/base-popup/BasePopup';
import { FetchColumnsType } from '@/components/common/table-base';
import TableFetch, { IObject } from '@/components/common/table-fetch';
import { EditorWithCounter } from '@/components/form-box-editor';
import { PreviewMissedLabel } from '@/components/form-select/InfinityFormSelect';
import { SelectCustom } from '@/components/form-select/SelectCustom';
import { StepsProcess } from '@/components/step-process/StepsProcess';

import { setAlertNotification } from '@/redux/globalReducer';

import useAuthorization, * as s from '@/hooks/useAuthorization';
import useFetch from '@/hooks/useFetch';
import { useFilterConfigs } from '@/hooks/useFilterConfigs';
import useMotPic from '@/hooks/useMotPic';
import useMutation from '@/hooks/useMutation';

import { ROUTER_IDS } from '@/utils/constants';
import { FORMAT_DATE_JP, NOTE_MAX_LENGTH, TYPE } from '@/utils/constants/AppConstants';
import {
  CREATE_NEW_PROJECT,
  NO_DATA_CREATE_NEW_PROJECT,
  NO_DATA_PROJECT_LIST,
  ONCE_YOU_START_ADDING_PROJECTS
} from '@/utils/constants/AuthorizationProjectManagement';
import { BATCH_REGISTRATION_PROJECT_URL, CREATE_PROJECT_URL, EDIT_PROJECT_URL, VIEW_PROJECT_URL } from '@/utils/constants/RouteContants';
import { fetchFilterConfigs } from '@/utils/helpers/routeLoaders';
import { stringifyObjectValidateYup } from '@/utils/method';
import { updateProjectAssignPic } from '@/utils/services/ProjectApiService';
import { PROJECT_STATUS_ID } from '@utils/constants/project';
import { DataViewer } from '@utils/helpers/common';
import { IProcesses } from '@utils/interfaces/ProjectListInterface';

import IconAddNew from '@/assets/icons/base-filter/icon-add-new.svg';
import DefaultEmptySvg from '@/assets/icons/table/base-table-empty-with-smile-face.svg';
import UploadIcon from '@assets/icons/Upload.svg';

import TableHeader, { getTotalFiltered } from './Header';
import { DEFAULT_FILTER_FORM, TYPE_OF_TASK } from './constant';
import { IFormFilter, IQueryFilterParams, IStatus, PROCESS_TRAVEL_NAME } from './models';
import { generateFilter } from './ultis';

import './Styles.scss';

const FILTER_CONFIG_MAP: Record<string, string> = {
  [TYPE_OF_TASK.ME]: ROUTER_IDS.PROJECT_LIST,
  [TYPE_OF_TASK.ALL]: `${ROUTER_IDS.PROJECT_LIST}_${TYPE_OF_TASK.ALL}`
};

export default function ProjectList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPermission, isExternalRole, isInternalRole, user } = useAuthorization();
  const { data: listStatus } = useFetch<IStatus[]>('/prj/status', 'GET');
  const { userMotPicOptions } = useMotPic({ showInactive: true });
  const { data: defaultFilterConfigs, update: updateFilterConfigs } = useFilterConfigs<FieldValues>(ROUTER_IDS.PROJECT_LIST);
  const formFilter = useForm<IFormFilter>({ defaultValues: generateDefaultFilterForm().form });
  const popupMotPicForm = useForm<{ messageForPic?: string }>({ defaultValues: { messageForPic: '' } });
  const [filter, setFilter] = useState(generateDefaultFilterForm().query);
  const pendingMotPicReason = useRef('');
  const popupEditorKey = useRef(0);
  const popupMotPicReasonLength = useRef(0);
  const { mutate: deleteProject } = useMutation(`/prj/projects`, {
    method: 'DELETE',
    bodyType: 'json',
    showToastSuccess: true,
    showToastError: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('questionnaire:actions:delete') }) ?? '',
    defaultErrorMsg: t('common:MSG_032') + t('common:error_asynchronous_data_delete')
  });

  function generateDefaultFilterForm() {
    const results = {
      form: {
        ...DEFAULT_FILTER_FORM,
        primary: {
          ...DEFAULT_FILTER_FORM.primary,
          picIds: user?.id && !isExternalRole ? [user?.id] : null
        }
      },
      query: {
        filter: generateFilter({
          keyword: '',
          picIds: user?.id && !isExternalRole ? [user?.id] : undefined,
          companyId: isExternalRole ? user?.organizationId : defaultFilterConfigs?.primary?.companyId
        })
      }
    };

    results.form = {
      picBy: defaultFilterConfigs?.picBy ?? TYPE_OF_TASK.ME,
      search: results.form?.search ?? defaultFilterConfigs?.search,
      primary: {
        ...results.form?.primary,
        ...defaultFilterConfigs?.primary
      }
    };
    const totalFiltered = getTotalFiltered(defaultFilterConfigs?.primary);
    const isFilterPic = isExternalRole ? false : results.form.picBy !== TYPE_OF_TASK.ALL;
    const isFiltering = Boolean(isFilterPic || results.form.search || totalFiltered);
    results.query = {
      filter: generateFilter({
        keyword: defaultFilterConfigs?.search ?? results.form?.search ?? '',
        isExceptStatus: !isFiltering,
        ...defaultFilterConfigs?.primary,
        ...results.form?.primary,
        companyId: isExternalRole ? user?.organizationId : defaultFilterConfigs?.primary?.companyId
      })
    };

    return results;
  }

  const handleUpdateFilterMe = async (values: FieldValues, res: any = {}) => {
    updateFilterConfigs(
      {
        ...res,
        picBy: values?.picBy,
        primary: {
          ...values?.primary,
          ...res?.primary,
          picIds: values?.primary?.picIds
        }
      },
      ROUTER_IDS.PROJECT_LIST
    );
  };

  useEffect(() => {
    const fetchFilter = async () => {
      if (!defaultFilterConfigs?.primary && (defaultFilterConfigs?.picBy === TYPE_OF_TASK.ME || !defaultFilterConfigs?.picBy)) {
        await handleUpdateFilterMe({
          ...DEFAULT_FILTER_FORM,
          primary: {
            ...DEFAULT_FILTER_FORM.primary,
            picIds: user?.id && !isExternalRole ? [user?.id] : null
          }
        });
      }
    };

    fetchFilter();
  }, [defaultFilterConfigs?.primary]);

  const updateMotPic = async (newValue: string, prjId: string, version: number, changedPicReason: string = '') => {
    const motPicType = userMotPicOptions.find((item: any) => item.value === newValue);
    try {
      const updateData = {
        id: prjId,
        picId: motPicType?.value,
        picName: motPicType?.label,
        picEmail: motPicType?.email,
        version,
        changedPicReason
      };
      await updateProjectAssignPic(prjId, updateData);
      setFilter({
        ...filter
      });
    } catch (error) {}
  };

  const handePreChangeMotPic = async (value: any) => {
    popupEditorKey.current += 1;
    popupMotPicReasonLength.current = 0;
    popupMotPicForm.reset({ messageForPic: '' });

    const handleMotPicReasonCharacterChange = (characterCount: number) => {
      popupMotPicReasonLength.current = characterCount;
      if (characterCount > NOTE_MAX_LENGTH) {
        popupMotPicForm.setError('messageForPic', {
          type: 'maxLength',
          message: stringifyObjectValidateYup({
            keyT: 'common:MSG_C_011',
            optionsTx: { field: 'project_detail:mot_pick:label_popup', limited: NOTE_MAX_LENGTH }
          })
        });
      } else {
        popupMotPicForm.clearErrors('messageForPic');
      }
    };

    const showPopup = await showBasePopup({
      type: 'confirm',
      onConfirm: () => {
        const characterCount = popupMotPicReasonLength.current;
        handleMotPicReasonCharacterChange(characterCount);
        return characterCount <= NOTE_MAX_LENGTH;
      },
      modalConfigs: { destroyOnClose: true },
      title: t(`common:MSG_P_009:title`) || '',
      msg: (
        <div>
          <div>{t('common:MSG_P_009:content')}</div>

          <div className='text-field-container mt-4'>
            <FormProvider {...popupMotPicForm}>
              <EditorWithCounter
                key={popupEditorKey.current}
                control={popupMotPicForm.control as any}
                label={t('project_detail:mot_pick:label_popup')}
                name='messageForPic'
                limit={NOTE_MAX_LENGTH}
                onCharacterChange={handleMotPicReasonCharacterChange}
                onChange={(content) => popupMotPicForm.setValue('messageForPic', content, { shouldDirty: true })}
                defaultValue=''
                className='ant-input ant-input-borderless input-style'
                placeholder={t('project_detail:mot_pick:placeholder_popup')}
                editorWrapperProps={{ style: { height: 220 } }}
              />
            </FormProvider>
          </div>
        </div>
      )
    });

    if (showPopup !== 'confirm') {
      popupMotPicForm.reset({ messageForPic: '' });
      throw new Error('');
    }

    const reason = popupMotPicForm.getValues('messageForPic') ?? '';
    popupMotPicForm.reset({ messageForPic: '' });
    pendingMotPicReason.current = reason;

    return value;
  };

  const getEmptyDataAlert = (): ReactNode => {
    return (
      <div className='flex flex-col items-center'>
        <span className='mt-[40px]'>
          <DefaultEmptySvg width={60} height={60} />
        </span>
        {hasPermission(CREATE_NEW_PROJECT, s.V) && (
          <p className='text-16 font-semibold text-[#12212E] my-1'>{t('project:list:create_new_project')}</p>
        )}
        {hasPermission(ONCE_YOU_START_ADDING_PROJECTS, s.V) && (
          <p className='text-base font-[400] text-[#525A68] mb-3'>{t('project:list:empty_project_table_description')}</p>
        )}
        {isInternalRole && (
          <BaseButton
            size='medium'
            icon={<IconAddNew style={{ verticalAlign: 'sub' }} />}
            onClick={() => {
              navigate(CREATE_PROJECT_URL);
            }}
            disabled={!hasPermission(NO_DATA_CREATE_NEW_PROJECT, s.C)}
          >
            {t('project:list:add_new_btn_label')}
          </BaseButton>
        )}
      </div>
    );
  };
  const columns: FetchColumnsType<IObject> = [
    {
      title: t('project:fields:project_id'),
      key: 'code',
      width: 232,
      className: 'max-w-[150px]',
      fixed: 'left',
      render: (_value: any, record: any) => {
        const code = () => {
          if (!record?.isPublished) {
            return (
              <span>
                {!record?.isDraft && isInternalRole && <span className='text-negative font-normal'>{t('announcement:list:label:unpublished')} </span>}
                {!record.id ? record.code : <span className='underline'>{record.code}</span>}
              </span>
            );
          }
          if (!record.id) return record.code;
          return <span className='underline'>{record.code}</span>;
        };

        const isDoneAllTask =
          !!record.processes?.length &&
          !record.processes.some((process: IProcesses) => process.name !== PROCESS_TRAVEL_NAME && !process.completedDate);
        const showStatusTag =
          !record.isDraft &&
          ([PROJECT_STATUS_ID.NOT_STARTED, PROJECT_STATUS_ID.COMPLETED, PROJECT_STATUS_ID.CANCELLED].includes(record.statusId) || isDoneAllTask);

        return (
          <div className='flex items-center gap-[8px]'>
            <AppTooltip title={record.id ? record.code : t('project:list:project_not_public')}>
              {!record.id ? (
                <div className='block truncate text-[14px]'>
                  {record?.isDraft ? <span className='text-negative text-[14px]'>{t('basic_information:isDraft')}</span> : ''}
                  <span className='text-[14px]'>{code()}</span>
                </div>
              ) : (
                <Link to={record?.isDraft ? EDIT_PROJECT_URL(record.id) : VIEW_PROJECT_URL(record.id)} style={{ display: 'inline' }}>
                  <div className='inline-block truncate text-[14px]'>
                    {record?.isDraft ? <span className='text-negative text-[14px]'>{t('basic_information:isDraft')}</span> : ''}
                    <span className='text-[14px]'>{code()}</span>
                  </div>
                </Link>
              )}
            </AppTooltip>
            {showStatusTag && <ProjectStatusTag statusId={isDoneAllTask ? PROJECT_STATUS_ID.COMPLETED : record.statusId} />}
          </div>
        );
      }
    },
    {
      title: t('project:fields:applicant_name'),
      fixed: 'left',
      dataIndex: 'applicantName',
      width: 180,
      render(_value: any, record: any) {
        const renderName = (isToolTip: boolean = false) =>
          record.applicantName
            ?.split('、')
            ?.map((name: string, index: number) => (
              <div key={`${name}-${index}`}>
                {name && <span className={classNames('block w-fit max-w-full', !isToolTip && 'truncate')}>{name}</span>}
              </div>
            ));
        return (
          <AppTooltip className='block max-w-full w-fit  text-[14px]' title={renderName(true)}>
            {renderName()}
          </AppTooltip>
        );
      }
    },
    {
      title: t('project:fields:company_name'),
      dataIndex: 'applicantCompanyName',
      width: 140,
      render(_value: any, record: any) {
        return (
          <AppTooltip title={record.applicantCompanyName}>
            <div className='max-w-full w-fit whitespace-pre-line'>{record.applicantCompanyName}</div>
          </AppTooltip>
        );
      }
    },
    {
      title: t('project:fields:country_of_travel'),
      dataIndex: 'applicantCountryName',
      key: 'country_of_travel',
      render(_value: any, record: any) {
        return (
          <AppTooltip className='block max-w-full w-fit truncate text-[14px]' title={record.applicantCountryName}>
            {record.applicantCountryName}
          </AppTooltip>
        );
      },
      width: 140
    },
    {
      title: t('project:fields:location'),
      dataIndex: 'placeOfMemo',
      render(_value: any, record: any) {
        return (
          <AppTooltip className='block max-w-full w-fit truncate text-[14px]' title={record.placeOfMemo}>
            {record.placeOfMemo}
          </AppTooltip>
        );
      },
      width: 100
    },
    {
      title: t('project:fields:visa_category'),
      dataIndex: 'visaCategoryName',
      render(_value: any, record: any) {
        return (
          <AppTooltip className='block max-w-full w-fit whitespace-pre-line text-[14px]' title={record.visaCategoryName}>
            {record.visaCategoryName}
          </AppTooltip>
        );
      },
      width: 110
    },
    {
      title: t('project:fields:destination_base_name'),
      dataIndex: 'assignmentCompanyName',
      key: 'destination_base_name',
      width: 160,
      render(_value: any, record: any) {
        return (
          <AppTooltip title={record.assignmentCompanyName}>
            <div className='max-w-full w-fit whitespace-pre-line'>{record.assignmentCompanyName}</div>
          </AppTooltip>
        );
      }
    },
    {
      title: t('project:fields:scheduled_travel_date'),
      key: 'departureDate',
      dataIndex: 'departureDate',
      render: (_value: any, record: any) => (
        <AppTooltip className='text-[14px]' title={record.departureDate && dayjs(record.departureDate).format(FORMAT_DATE_JP)}>
          {record.departureDate && dayjs(record.departureDate).format(FORMAT_DATE_JP)}
        </AppTooltip>
      ),
      sorter: true,
      width: 140,
      sortOrder: 'ascend'
    },
    {
      title: t('project:fields:mot_pic'),
      dataIndex: 'picId',
      key: 'picId',
      render(value: string, prj: any) {
        const label = prj?.picName ? (prj?.picStatus ? prj.picName : `(${t('account_list:status_account:inactive')})${prj.picName}`) : '';
        return isExternalRole ? (
          DataViewer.display(label)
        ) : (
          <div className='mot-pick relative'>
            <PreviewMissedLabel options={userMotPicOptions} missedValues={[value]}>
              <SelectCustom
                className='w-full'
                labelInValue
                options={userMotPicOptions.filter((item) => item.status)}
                allowClear={false}
                preChange={handePreChangeMotPic}
                onChange={(value: any) => {
                  const selectedValue = value?.value ?? value;
                  updateMotPic(selectedValue, prj.id, prj.version, pendingMotPicReason.current);
                  pendingMotPicReason.current = '';
                }}
                value={
                  prj.picId
                    ? {
                        value: prj.picId,
                        label
                      }
                    : null
                }
                key='value'
                title=''
              />
            </PreviewMissedLabel>
          </div>
        );
      },
      tooltip: (_value: any, record: any) => {
        let label = '';
        if (record?.picName) {
          label = record.picStatus ? record.picName : `(${t('account_list:status_account:inactive')})${record.picName}`;
        }
        return label;
      },
      width: 170
    },
    {
      title: t('project:fields:process'),
      dataIndex: 'process',
      render(_: any, record: any) {
        return (
          <StepsProcess
            stepData={record.processes}
            listStatus={listStatus || []}
            disabled={[PROJECT_STATUS_ID.COMPLETED, PROJECT_STATUS_ID.CANCELLED].includes((record.statusId as PROJECT_STATUS_ID) ?? '')}
            active={record.active}
          />
        );
      },
      width: 755
    }
  ];

  const getFilterConfigId = (type: string): string => FILTER_CONFIG_MAP[type] ?? ROUTER_IDS.PROJECT_LIST;

  const getFilterConfig = async (picBy: string) => {
    const id = getFilterConfigId(picBy);
    const response = await fetchFilterConfigs(id);
    return response || {};
  };

  const normalizePrimaryValues = async (values: FieldValues, isExternal: boolean) => {
    let newValues: any = {
      ...values
    };
    if (isExternal) {
      delete newValues?.primary?.isDraft;
      return { ...newValues, picBy: TYPE_OF_TASK.ALL, primary: { ...newValues?.primary, picIds: null } };
    }

    if (!newValues?.primary?.picIds?.length && formFilter.getValues('picBy') === TYPE_OF_TASK.ME) {
      newValues = {
        ...values,
        primary: { ...newValues?.primary, picIds: [user?.id] }
      };
      updateFilterConfigs(
        {
          ...newValues
        },
        getFilterConfigId(TYPE_OF_TASK?.ME)
      );

      newValues = await getFilterConfig(TYPE_OF_TASK.ALL);

      if (!Object.keys(newValues)?.length) {
        newValues = {
          picBy: TYPE_OF_TASK.ALL,
          primary: DEFAULT_FILTER_FORM.primary,
          search: values?.search || ''
        };
      } else {
        newValues.search = values?.search || newValues.search || '';
      }
      formFilter.reset({ ...newValues });
    }

    return newValues;
  };

  const buildFormValues = (updatedValues: any, user: any, isExternal: boolean): IQueryFilterParams => {
    const totalFiltered = getTotalFiltered(updatedValues.primary);
    const isFilterPic = isExternal ? false : updatedValues.picBy !== TYPE_OF_TASK.ALL;
    const isFiltering = Boolean(isFilterPic || updatedValues.search || totalFiltered);

    return {
      keyword: updatedValues.search,
      isExceptStatus: !isFiltering,
      ...updatedValues.primary,
      companyId: isExternal ? user?.organizationId : updatedValues.primary.companyId
    };
  };

  const handleNormalUpdate = async (values: FieldValues, picBy: string, isExternal: boolean) => {
    const updatedValues = await normalizePrimaryValues(values, isExternal);

    if (!(!values?.primary?.picIds?.length && values.picBy === TYPE_OF_TASK.ME)) {
      updateFilterConfigs({ ...updatedValues }, getFilterConfigId(isExternal ? TYPE_OF_TASK.ALL : picBy));
    } else {
      updateFilterConfigs({ ...updatedValues }, getFilterConfigId(TYPE_OF_TASK.ALL));
    }

    const formValues: IQueryFilterParams = buildFormValues(updatedValues, user, isExternal);
    setFilter({ filter: generateFilter(formValues) });
  };

  const handleChangeView = async (values: FieldValues, picBy: string) => {
    let res = (await getFilterConfig(picBy)) || {};

    updateFilterConfigs(
      {
        ...res,
        picBy,
        primary: {
          ...DEFAULT_FILTER_FORM.primary,
          ...res?.primary
        }
      },
      getFilterConfigId(picBy)
    );

    if (!Object.keys(res)?.length) {
      res = {
        picBy,
        search: values?.search || '',
        primary: DEFAULT_FILTER_FORM.primary
      };
    } else {
      res.search = values?.search || res.search || '';
    }

    const formValues: IQueryFilterParams = buildFormValues(res, user, isExternalRole);

    formFilter.reset({ ...res, search: res.search ?? '', picBy });
    setFilter({ filter: generateFilter(formValues || {}) });
  };

  const updateFilter = async (values: FieldValues, isChangeView?: boolean) => {
    const picBy = values?.picBy;
    if (isChangeView) {
      await handleChangeView(values, picBy);
    } else {
      await handleNormalUpdate(values, picBy, isExternalRole);
    }
  };

  const handleDelete = async (keys: Key[], records: any[]) => {
    const idsToDelete = records.map((itemDocument) => {
      return {
        id: itemDocument.id || '',
        version: itemDocument.version || ''
      };
    });
    if (!idsToDelete.length) {
      return 0;
    }
    const result = await deleteProject({
      projects: idsToDelete
    });
    if (!result) return 0;
    const { data } = result;
    if (data.successCount === keys.length) {
      dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.SUCCESS,
          message: t('common:MSG_C_003', { item: t('questionnaire:actions:delete') })
        })
      );
    }

    if (data.successCount < keys.length) {
      dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.SUCCESS,
          message: t('common:MSG_C_020', { deletable: data.successCount, total: keys.length })
        })
      );
    }
    return data.successCount;
  };

  return (
    <div>
      <div className='grid grid-cols-2 gap-4 page-list-header mb-[12px]'>
        <div className='title-24'>{t('project:list:page_title')}</div>
        <div className='flex items-center gap-[16px] ml-auto'>
          {isInternalRole && (
            <BaseButton
              type='secondary'
              size='medium'
              icon={<UploadIcon />}
              className='w-[120px]'
              onClick={() => {
                navigate(BATCH_REGISTRATION_PROJECT_URL);
              }}
            >
              {t('batch_registration:title')}
            </BaseButton>
          )}
          <BaseButton
            size='medium'
            icon={<IconAddNew style={{ verticalAlign: 'sub' }} />}
            onClick={() => {
              navigate(CREATE_PROJECT_URL);
            }}
          >
            {t('project:list:add_new_btn_label')}
          </BaseButton>
        </div>
      </div>
      <div className='page-content'>
        <div className='data-project-table'>
          <TableFetch
            tableHeader={
              <FormProvider {...formFilter}>
                <TableHeader onSubmit={updateFilter} />
              </FormProvider>
            }
            apiEndpoint='/prj/projects/search'
            defaultSort={[
              { field: 'isDraft', order: 'descend' },
              { field: 'departureDate', order: 'ascend' }
            ]}
            apiMethod='POST'
            showAddLine={false}
            columns={columns}
            filterDefault={filter}
            rowSelection={
              isExternalRole
                ? {
                    getCheckboxProps: (record) => ({
                      disabled: !record.isDraft
                    })
                  }
                : {}
            }
            scrollTable={{ x: 0, maxHeight: '100vh - 248px' }}
            showDelete={hasPermission(s.DELETE_PROJECT_LIST, s.V)}
            showDownload={false}
            handleDeleteClick={hasPermission(s.DELETE_PROJECT_LIST, s.C) ? handleDelete : undefined}
            deleteConfirmPopup={{
              type: TYPE.DISCARD,
              title: t('common:MSG_029.1_title', { item: t('basic_information:project') }) ?? '',
              msg:
                `${t('common:MSG_029.1_description', { item: t('basic_information:project') })} ${t('common:MSG_029_description2', {
                  item: t('basic_information:project')
                })}` ?? ''
            }}
            emptyDataWithoutFilter={hasPermission(NO_DATA_PROJECT_LIST, s.V) && getEmptyDataAlert()}
          />
        </div>
      </div>
    </div>
  );
}
